import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/Surface";
import { Results } from "../components/Paged";
import { Avatar } from "../components/Avatar";
import { Stars } from "../components/Stars";
import { Icon } from "../app/Icon";
import { labelize } from "../lib/format";
import { searchDirectory, DIRECTORY_TYPES, TRUST_TONE, type DirectoryProfile } from "../lib/directory";

const tileTone: Record<string, string> = {
  driver: "bg-verified-bg text-verified",
  company: "bg-info/15 text-info",
  recruiter: "bg-accent/15 text-accent-strong",
  broker_dispatcher: "bg-surface-2 text-muted",
};

function Card({ p }: { p: DirectoryProfile }) {
  return (
    <Link to={`/p/${p.id}`} className="grid gap-3 rounded-card border border-line bg-surface p-5 transition-colors hover:border-accent">
      <div className="flex items-center gap-3">
        <Avatar src={p.image_url} name={p.display_name ?? labelize(p.type)} className="h-12 w-12 rounded-lg text-lg" tone={tileTone[p.type] ?? tileTone.recruiter} />
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-black tracking-tight">{p.display_name ?? "Member"}</h3>
          <p className="font-mono text-[11px]">
            <span className="text-muted">{labelize(p.type)}</span>
            {p.trust_level && p.trust_level !== "unverified" && (
              <span className={TRUST_TONE[p.trust_level]}> · {p.trust_label?.toUpperCase()} ✓</span>
            )}
          </p>
          {p.rating != null && (p.review_count ?? 0) > 0 && <Stars value={p.rating} count={p.review_count} className="mt-0.5" />}
        </div>
      </div>
      {p.headline && <p className="line-clamp-2 text-sm text-muted">{p.headline}</p>}
    </Link>
  );
}

export function Directory() {
  const { type: routeType } = useParams();
  const type = routeType ?? "";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get("q") ?? "";
  const [q, setQ] = useState(urlQ);
  const [profiles, setProfiles] = useState<DirectoryProfile[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setQ(urlQ);
    let cancelled = false;
    setLoading(true);
    searchDirectory(type || undefined, urlQ || undefined)
      .then((r) => {
        if (cancelled) return;
        setProfiles(r.profiles);
        setCounts(r.counts);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [type, urlQ]);

  function pick(t: string) {
    const suffix = urlQ ? `?q=${encodeURIComponent(urlQ)}` : "";
    navigate(t ? `/network/${t}${suffix}` : `/network${suffix}`);
  }
  function onSearch(e: FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (q.trim()) p.set("q", q.trim());
    else p.delete("q");
    setSearchParams(p);
  }

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const title = type ? DIRECTORY_TYPES.find((t) => t.key === type)?.label ?? "Network" : "Search / Network";

  return (
    <div className="grid gap-6">
      <PageHeader title={title} subtitle="Find verified drivers, carriers, recruiters, and brokers across the network." />

      <form className="relative flex max-w-xl items-center" onSubmit={onSearch}>
        <Icon name="search" className="pointer-events-none absolute left-3 h-4 w-4 text-muted" />
        <input
          className="w-full rounded-lg border border-line bg-surface-2 py-2 pl-9 pr-3 text-sm text-ink"
          placeholder="Search by name or headline…"
          aria-label="Search the network"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </form>

      <div className="flex flex-wrap gap-2">
        {DIRECTORY_TYPES.map((t) => (
          <button
            key={t.key || "all"}
            type="button"
            onClick={() => pick(t.key)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-bold transition-colors ${type === t.key ? "border-accent bg-accent/10 text-accent-strong" : "border-line text-muted hover:text-ink"}`}
          >
            {t.label} ({t.key ? counts[t.key] ?? 0 : total})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : (
        <Results
          items={profiles}
          perPage={12}
          defaultView="grid"
          gridClass="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          renderCard={(p) => <Card key={p.id} p={p} />}
          renderRow={(p) => <Card key={p.id} p={p} />}
          empty={<p className="font-mono text-sm text-muted">No members match your search.</p>}
        />
      )}
    </div>
  );
}
